import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trophy } from "lucide-react";
import { toast } from "sonner";

import {
  BottomSheet, ChalkActionButton, ChalkCard, DarkHeader, EmptyState,
  SectionTitle,
} from "../components/chalk/primitives";
import { tournaments } from "../lib/tournamentApi";
import { SPORT_OPTIONS, sportLabel } from "../lib/sports";


/**
 * Director's list of tournaments they own or help with.
 * Phase 1 — Whiteboard tier (free).
 */
export default function Tournaments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  async function load() {
    try {
      const d = await tournaments.list();
      setRows(d.tournaments || []);
    } catch (e) {
      toast.error(e.message || "Couldn't load tournaments.");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  return (
    <>
      <DarkHeader title="Tournaments" subtitle="Draw it up. Run it tight." />
      <div style={{ padding: "16px 18px 100px" }} data-testid="tournaments-list">
        <a href="/app/director" data-testid="tournaments-director-link" style={{ display: "inline-block", fontSize: 12, fontWeight: 700, color: "var(--green-mid)", marginBottom: 10 }}>
          Director plans & billing →</a>
        {loading ? null : rows.length === 0 ? (
          <EmptyState
            icon={<Trophy size={42} aria-hidden="true" />}
            headline="No tournaments yet."
            body="Spin one up — bracket, score entry, and a shareable link, free up to 8 teams."
            ctaLabel="Create tournament"
            onCta={() => setOpen(true)}
          />
        ) : (
          <>
            <SectionTitle>Yours</SectionTitle>
            {rows.map((t) => (
              <ChalkCard
                key={t.id}
                data-testid={`tournament-row-${t.id}`}
                onClick={() => nav(`/app/tournaments/${t.id}`)}
                style={{ marginBottom: 10, cursor: "pointer" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 600, color: "var(--chalk-board)" }}>
                      {t.name}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 3 }}>
                      {sportLabel(t.sport, t.sport_custom)} · {t.team_type} · {t.status} · {t.role}
                    </div>
                  </div>
                  <div style={{ fontSize: 10, textTransform: "uppercase",
                                color: "var(--green-mid)", fontWeight: 700 }}>
                    {t.bracket_locked ? "LIVE" : "DRAFT"}
                  </div>
                </div>
              </ChalkCard>
            ))}
          </>
        )}
      </div>
      <button
        aria-label="New tournament"
        data-testid="tournament-new-fab"
        onClick={() => setOpen(true)}
        style={{
          position: "fixed", right: 20, bottom: 92, width: 56, height: 56,
          borderRadius: "50%", background: "var(--green-mid)", color: "#F5F0E8",
          border: "none", cursor: "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        }}
      >
        <Plus size={24} aria-hidden="true" />
      </button>
      <CreateTournamentSheet
        isOpen={open}
        onDismiss={() => setOpen(false)}
        onCreated={(t) => { setOpen(false); nav(`/app/tournaments/${t.id}`); }}
      />
    </>
  );
}


function CreateTournamentSheet({ isOpen, onDismiss, onCreated }) {
  const [name, setName] = useState("");
  const [sport, setSport] = useState("baseball");
  const [sportCustom, setSportCustom] = useState("");
  const [teamType, setTeamType] = useState("youth");
  const [date, setDate] = useState(() => {
    const d = new Date(); return d.toISOString().slice(0, 10);
  });
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (name.trim().length < 2) {
      toast.error("Name it something people can remember.");
      return;
    }
    setBusy(true);
    try {
      const d = await tournaments.create({
        name: name.trim(), sport, team_type: teamType,
        sport_custom: sport === "other" ? sportCustom.trim() || null : null,
        starts_on: new Date(`${date}T12:00:00Z`).toISOString(),
        location: location.trim() || null,
      });
      toast.success("Tournament created.");
      onCreated(d.tournament);
    } catch (e) {
      toast.error(e.message || "Couldn't create.");
    } finally { setBusy(false); }
  }

  return (
    <BottomSheet isOpen={isOpen} onDismiss={onDismiss}
                 title="New tournament"
                 testId="create-tournament-sheet">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <label>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em",
                        color: "var(--chalk-muted)", fontWeight: 700, marginBottom: 4 }}>Name</div>
          <input value={name} onChange={(e) => setName(e.target.value)}
                 data-testid="t-name"
                 placeholder="Spring Classic"
                 className="chalk-input"
                 style={{ width: "100%", border: "0.5px solid var(--chalk-border)",
                          borderRadius: 10, padding: "11px 12px", fontSize: 16,
                          background: "#FBF8F2", color: "var(--chalk-board)", outline: "none" }} />
        </label>
        {sport === "other" && (
          <input value={sportCustom} onChange={(e) => setSportCustom(e.target.value)} placeholder="Sport name (e.g. Ultimate)"
                 data-testid="t-sport-custom" maxLength={30}
                 style={{ border: "0.5px solid var(--chalk-border)", borderRadius: 10, padding: "10px 12px",
                          background: "#FBF8F2", color: "var(--chalk-board)" }} />
        )}
        <div style={{ display: "flex", gap: 6 }}>
          <select value={sport} onChange={(e) => setSport(e.target.value)}
                  data-testid="t-sport"
                  style={{ flex: 1, padding: "10px 12px", borderRadius: 10,
                           border: "0.5px solid var(--chalk-border)",
                           background: "#FBF8F2", color: "var(--chalk-board)" }}>
            {SPORT_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={teamType} onChange={(e) => setTeamType(e.target.value)}
                  data-testid="t-team-type"
                  style={{ flex: 1, padding: "10px 12px", borderRadius: 10,
                           border: "0.5px solid var(--chalk-border)",
                           background: "#FBF8F2", color: "var(--chalk-board)" }}>
            <option value="youth">Youth</option>
            <option value="adult">Adult</option>
          </select>
        </div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
               data-testid="t-date"
               style={{ padding: "10px 12px", borderRadius: 10,
                        border: "0.5px solid var(--chalk-border)",
                        background: "#FBF8F2", color: "var(--chalk-board)" }} />
        <input value={location} onChange={(e) => setLocation(e.target.value)}
               data-testid="t-location"
               placeholder="Location (optional)"
               style={{ padding: "10px 12px", borderRadius: 10,
                        border: "0.5px solid var(--chalk-border)",
                        background: "#FBF8F2", color: "var(--chalk-board)" }} />
        <ChalkActionButton variant="primary" label="Create" loading={busy} onClick={submit}
                           testId="t-create-submit" />
      </div>
    </BottomSheet>
  );
}
